import React, { memo } from 'react';
import { Chip, Typography } from '@mui/material';
import {
  Edit as EditIcon,
  PlayArrow as PlayIcon,
  Delete as DeleteIcon,
  Sync as SyncIcon,
  FolderOff as FolderMinusIcon,
} from '@mui/icons-material';
import { ReactComponent as ToolIcon } from 'new/components/icons/tool.svg';
import { IndividualTool } from '../../types/ui';
import { BaseCard } from '../shared';
import styles from './ToolCard.module.scss';

interface ToolCardProps {
  tool: IndividualTool;
  isSelected: boolean;
  onSelect: (toolId: string, selected: boolean) => void;
  onAction: (action: string, toolId: string) => void;
  onOpenDetails: (tool: IndividualTool) => void;
  inPackage?: boolean;
}

const ToolCard: React.FC<ToolCardProps> = memo(
  ({ tool, isSelected, onSelect, onAction, onOpenDetails, inPackage }) => {
    const menuItems = [
      {
        icon: <EditIcon fontSize="small" className={styles.actionButtonIcon} />,
        label: 'Edit',
        onClick: () => onAction('edit', tool.id),
      },
      {
        icon: <SyncIcon fontSize="small" className={styles.actionButtonIcon} />,
        label: 'Reconcile',
        onClick: () => onAction('reconcile', tool.id),
      },
      {
        icon: <PlayIcon fontSize="small" className={styles.actionButtonIcon} />,
        label: 'Test Tool',
        onClick: () => onAction('test', tool.id),
      },
      ...(inPackage
        ? [
            {
              icon: <FolderMinusIcon fontSize="small" />,
              label: 'Remove from Package',
              onClick: () => onAction('removeFromPackage', tool.id),
            },
          ]
        : []),
    ];

    // Status badge style based on versionless status (init/paused/active)
    const getStatusStyle = () => {
      switch (tool.uiStatus) {
        case 'active':
          return styles.active;
        case 'paused':
          return styles.paused;
        case 'init':
        default:
          return styles.init;
      }
    };

    const badges = [
      <Chip
        key="status"
        label={tool.statusLabel || tool.uiStatus}
        size="small"
        className={`${styles.statusBadge} ${getStatusStyle()}`}
      />,
    ];

    return (
      <BaseCard
        icon={<ToolIcon />}
        title={
          <Typography variant="body2" fontWeight={500}>
            {tool.name}
          </Typography>
        }
        subtitle={
          <Typography variant="caption" color="text.secondary">
            {tool.nexsetName}
          </Typography>
        }
        badges={badges}
        selectable={true}
        selected={isSelected}
        onSelect={(selected) => onSelect(tool.id, selected)}
        infoButton={true}
        infoTooltip="View Details"
        onInfo={() => onOpenDetails(tool)}
        menuItems={menuItems}
        inPackage={inPackage}
      />
    );
  },
);

ToolCard.displayName = 'ToolCard';

export default ToolCard;
